import { HttpClient, HttpEvent, HttpEventType } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '@env/environment';
import { IDocument } from 'app/models/documents/document';
import { IDocumentType } from 'app/models/documents/documentType';
import { concatMap, filter, from, map, Observable, startWith } from 'rxjs';

export interface UploadProgress {
  progress: number;
  completed: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class PublicDocumentService {

  private readonly chunkSize = 5 * 1024 * 1024; // 5 MB
  public api: string = environment.publicApi + 'PublicDocument/'
  private http = inject(HttpClient);

  getDocumentTypes(types: string[]): Observable<IDocumentType[]>{
    return this.http.post<IDocumentType[]>(this.api + 'GetDocumentTypes', types);
  }

  createDocumentRecord(document: IDocument, entityType: string): Observable<IDocument>{
    return this.http.post<IDocument>(this.api + 'CreateDocumentRecord/' + entityType, document)
  }

  uploadFile(file: File): Observable<UploadProgress> {
    const uploadId = crypto.randomUUID();
    const totalChunks = Math.ceil(file.size / this.chunkSize);
    const chunks = Array.from({ length: totalChunks }, (_, i) => i);
    let totalUploadedBytes = 0;

    return from(chunks).pipe(
      concatMap(chunkIndex => {
        const start = chunkIndex * this.chunkSize;
        const end = Math.min(start + this.chunkSize, file.size);
        const chunk = file.slice(start, end);

        const formData = new FormData();
        formData.append('file', chunk);
        formData.append('fileName', file.name);
        formData.append('chunkIndex', chunkIndex.toString());
        formData.append('totalChunks', totalChunks.toString());
        formData.append('uploadId', uploadId);

        return this.http.post(this.api + 'UploadChunk', formData, {
          reportProgress: true,
          observe: 'events'
        }).pipe(
          filter((event: HttpEvent<any>) =>
            event.type === HttpEventType.UploadProgress ||
            event.type === HttpEventType.Response
          ),
          map((event: HttpEvent<any>): UploadProgress => {
            if (event.type === HttpEventType.UploadProgress) {
              const chunkProgress = event.loaded ?? 0;
              const overall = ((totalUploadedBytes + chunkProgress) / file.size) * 100;
              return { progress: Math.round(overall), completed: false };
            }

            totalUploadedBytes += chunk.size;
            const overall = (totalUploadedBytes / file.size) * 100;
            return {
              progress: Math.round(overall),
              completed: totalUploadedBytes === file.size
            };
          }),
          // optional: only if you want an explicit initial emission
          startWith({ progress: 0, completed: false })
        );
      })
    );
  }
}

